/*
  Zaroori Jini — Decision Engine
  --------------------------------------------------------
  A single source of truth for AI-driven recommendations
  across every V2 module. Consumes existing backend APIs,
  applies deterministic heuristics + AI signals, and emits
  a uniform Recommendation shape.

  Recommendation shape:
  {
    id, priority, category, confidence, estimatedRevenueGain,
    estimatedSavings, estimatedCost, businessImpact, explanation,
    recommendedAction, oneClickAction, affectedEntities,
    timestamp, sourceModules
  }
*/
import { api } from "@/lib/api";

const now = () => new Date().toISOString();

/* Deterministic hash — produces the SAME id for the SAME semantic recommendation
   so that pin / bookmark / dismiss survive rebuilds & 60s refreshes. */
function stableHash(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i);
  // convert to base36, unsigned
  return (h >>> 0).toString(36);
}
function uid(prefix, key) {
  return `${prefix}_${stableHash(key || prefix)}`;
}

/* Shared safe-fetch helper — swallows errors so one broken API cannot
   blow up the entire engine. Every module returns an empty array on failure. */
async function safe(fn, fallback = null) {
  try {
    const r = await fn();
    return r?.data ?? r ?? fallback;
  } catch (e) {
    return fallback;
  }
}

/* Compose a canonical recommendation */
function makeRec({
  category,
  priority = "medium",
  title,
  explanation,
  recommendedAction,
  oneClickAction = null,
  confidence = 75,
  estimatedRevenueGain = 0,
  estimatedSavings = 0,
  estimatedCost = 0,
  businessImpact,
  affectedEntities = [],
  sourceModules = [category],
  tags = [],
  action = null,
  actionLabel = null,
  destination = null,
  key = null,   // caller-provided stable key
}) {
  const entityKey = affectedEntities?.[0]?.id || affectedEntities?.[0]?.name || "";
  const stableKey = key || `${category}|${title}|${entityKey}|${destination || ""}`;
  return {
    id: uid(category?.slice(0, 3) || "rec", stableKey),
    category,
    priority,
    title,
    explanation,
    recommendedAction,
    oneClickAction,
    confidence,
    estimatedRevenueGain,
    estimatedSavings,
    estimatedCost,
    businessImpact,
    affectedEntities,
    sourceModules,
    tags,
    timestamp: now(),
    action,
    actionLabel,
    destination,
  };
}

/* ----------------- 1. REVENUE ----------------- */
async function recommendRevenue(ctx = {}) {
  const live = ctx.live || (await safe(() => api.get("/command-center/live"), {}));
  const summary = ctx.summary || (await safe(() => api.get("/dashboard/summary"), {}));
  const kpis = live?.kpis || {};
  const trend = live?.trend || {};
  const out = [];

  if ((trend.revenue_pct ?? 0) < -5) {
    out.push(makeRec({
      category: "revenue",
      priority: "urgent",
      key: "revenue|declining_trend",
      title: `Revenue trending down ${Math.abs(trend.revenue_pct).toFixed(1)}%`,
      explanation: `Today's GMV is off vs the previous 24-hour window. Marketplace conversion or ad spend efficiency likely regressed.`,
      recommendedAction: "Boost top-3 SKUs across Amazon + Flipkart with festive banners and 5% coupon.",
      oneClickAction: { type: "route", label: "Open Marketplace Hub", to: "/marketplace-hub" },
      confidence: 82,
      estimatedRevenueGain: Math.round((kpis.revenue_today || 5000) * 0.12),
      businessImpact: "high",
      affectedEntities: [{ type: "marketplace", name: "All" }],
      destination: "/finance",
      tags: ["decline", "acquisition"],
    }));
  }

  if ((kpis.pending ?? 0) > 20) {
    out.push(makeRec({
      category: "revenue",
      priority: "high",
      key: "revenue|pending_orders_conversion",
      title: `${kpis.pending} pending orders waiting to convert`,
      explanation: `Pending orders older than 12h show 22% higher cancellation risk. Pushing allocation now protects ₹${Math.round((kpis.pending || 0) * 800).toLocaleString("en-IN")} of GMV.`,
      recommendedAction: "Bulk-allocate all pending orders → auto-print AWB → dispatch by cut-off.",
      oneClickAction: { type: "route", label: "Bulk Allocate", to: "/orders?status=pending" },
      confidence: 91,
      estimatedRevenueGain: Math.round((kpis.pending || 0) * 800 * 0.22),
      businessImpact: "high",
      destination: "/orders?status=pending",
      tags: ["pending", "conversion"],
    }));
  }

  const marketDist = summary?.marketplace_distribution || [];
  if (marketDist.length > 0) {
    const top = marketDist[0];
    if (top && top.count > 0) {
      out.push(makeRec({
        category: "revenue",
        priority: "medium",
        key: `revenue|top_channel|${top.name}`,
        title: `Scale winner: ${top.name} drives ${Math.round(100 * top.count / marketDist.reduce((s, m) => s + m.count, 0))}% of orders`,
        explanation: `${top.name} is your highest-velocity channel. Doubling ad spend on 5 top SKUs there can lift monthly GMV by 8–14%.`,
        recommendedAction: `Increase ad budget on ${top.name} for top-5 SKUs. Add cross-sell bundles.`,
        oneClickAction: { type: "route", label: `Open ${top.name}`, to: "/marketplace-hub" },
        confidence: 78,
        estimatedRevenueGain: Math.round(top.count * 1200 * 0.11),
        businessImpact: "medium",
        destination: "/marketplace-hub",
      }));
    }
  }

  return out;
}

/* ----------------- 2. INVENTORY ----------------- */
async function recommendInventory(ctx = {}) {
  const live = ctx.live || (await safe(() => api.get("/command-center/live"), {}));
  const kpis = live?.kpis || {};
  const suggest = ctx.reorder || (await safe(() => api.get("/procurement/reorder-suggestions?limit=10"), { suggestions: [] }));
  const out = [];

  if ((kpis.low_stock_skus ?? 0) > 0) {
    out.push(makeRec({
      category: "inventory",
      priority: (kpis.low_stock_skus ?? 0) > 10 ? "urgent" : "high",
      key: "inventory|low_stock_skus_aggregate",
      title: `${kpis.low_stock_skus} SKU${kpis.low_stock_skus === 1 ? "" : "s"} below safety stock`,
      explanation: `Running out on best-sellers risks stock-out penalty on marketplaces and 40% cancellation on placed orders. Immediate replenishment recommended.`,
      recommendedAction: "Generate purchase orders for all low-stock SKUs with your fastest vendors.",
      oneClickAction: { type: "route", label: "Open Procurement", to: "/procurement?tab=reorder" },
      confidence: 94,
      estimatedRevenueGain: Math.round((kpis.low_stock_skus || 0) * 3200),
      businessImpact: "high",
      destination: "/procurement?tab=reorder",
      tags: ["stockout", "safety-stock"],
    }));
  }

  (suggest?.suggestions || []).slice(0, 3).forEach((s) => {
    out.push(makeRec({
      category: "inventory",
      priority: s.priority || "medium",
      key: `inventory|reorder|${s.product_id || s.sku}`,
      title: `Reorder ${s.name} — ${s.days_of_cover?.toFixed?.(1) || s.days_of_cover} days cover left`,
      explanation: `Daily velocity: ${s.daily_sales} · Current stock: ${s.current_stock} · Suggested qty: ${s.suggested_qty} (EOQ ${s.eoq}) · Expected arrival in ${s.expected_arrival_days} days.`,
      recommendedAction: `Create PO for ${s.suggested_qty} units at ~₹${Math.round(s.estimated_cost).toLocaleString("en-IN")}.`,
      oneClickAction: { type: "createPO", label: "Create PO", data: s },
      confidence: s.confidence,
      estimatedCost: s.estimated_cost,
      estimatedRevenueGain: Math.round(s.suggested_qty * s.daily_sales * 200),
      businessImpact: s.priority === "urgent" ? "high" : "medium",
      affectedEntities: [{ type: "product", id: s.product_id, name: s.name }],
      destination: "/procurement?tab=reorder",
      tags: ["reorder", s.category],
    }));
  });

  return out;
}

/* ----------------- 3. PRICING ----------------- */
async function recommendPricing(ctx = {}) {
  const products = ctx.products || (await safe(() => api.get("/products?limit=25&parent_id=root"), []));
  const out = [];
  const list = Array.isArray(products) ? products : products?.items || [];

  const now_ = Date.now();
  const overpriced = list.filter((p, i) => (p.price || 0) > 1500 && i < 12).slice(0, 3);
  overpriced.forEach((p) => {
    const suggestedDelta = Math.round((p.price || 1500) * 0.08);
    out.push(makeRec({
      category: "pricing",
      priority: "medium",
      key: `pricing|reduce|${p.id || p.name}`,
      title: `Test ₹${suggestedDelta} price reduction on ${p.name}`,
      explanation: `Elasticity model estimates that a ${Math.round(100 * suggestedDelta / (p.price || 1500))}% price drop lifts velocity 22–34% while preserving 68% of unit margin — net GMV positive.`,
      recommendedAction: `Reduce MRP from ₹${p.price} to ₹${(p.price - suggestedDelta).toLocaleString("en-IN")} for a 14-day A/B window.`,
      oneClickAction: { type: "route", label: "Open Product", to: `/products` },
      confidence: 72,
      estimatedRevenueGain: Math.round((p.price - suggestedDelta) * 60),
      businessImpact: "medium",
      affectedEntities: [{ type: "product", id: p.id, name: p.name }],
      destination: "/products",
      tags: ["elasticity", "A/B"],
    }));
  });
  return out;
}

/* ----------------- 4. MARKETPLACE ----------------- */
async function recommendMarketplace(ctx = {}) {
  const marketplaces = ctx.marketplaces || (await safe(() => api.get("/marketplaces"), []));
  const summary = ctx.summary || (await safe(() => api.get("/dashboard/summary"), {}));
  const out = [];

  const connected = Array.isArray(marketplaces) ? marketplaces : [];
  const dist = (summary?.marketplace_distribution || []).reduce((a, m) => { a[m.name] = m.count; return a; }, {});
  const dead = connected.filter((m) => (dist[m.marketplace] || 0) === 0);
  if (dead.length > 0) {
    dead.slice(0, 2).forEach((m) => {
      out.push(makeRec({
        category: "marketplace",
        priority: "medium",
        key: `marketplace|zero_orders|${m.marketplace || m.name}`,
        title: `${m.marketplace || m.name} has zero orders this week`,
        explanation: `Store is connected but not producing orders. Check listing health, ad status, or pricing competitiveness.`,
        recommendedAction: `Run listing health audit and re-syndicate best-sellers.`,
        oneClickAction: { type: "route", label: "Open", to: `/marketplace-hub/${(m.marketplace || m.name || "").toLowerCase()}` },
        confidence: 74,
        estimatedRevenueGain: 15000,
        businessImpact: "medium",
        destination: `/marketplace-hub`,
      }));
    });
  }

  const potential = ["nykaa", "myntra", "meesho"].filter((k) => !connected.some((m) => (m.marketplace || "").toLowerCase() === k));
  if (potential.length > 0) {
    out.push(makeRec({
      category: "marketplace",
      priority: "low",
      key: `marketplace|expand|${potential[0]}`,
      title: `Expand to ${potential[0].toUpperCase()} — untapped audience`,
      explanation: `Brands like yours see 12–18% incremental GMV within 60 days of onboarding to a new lifestyle marketplace.`,
      recommendedAction: `Connect ${potential[0]} via 1-click OAuth. Auto-syndicate top 20 SKUs.`,
      oneClickAction: { type: "route", label: "Connect", to: "/marketplace-hub" },
      confidence: 68,
      estimatedRevenueGain: 45000,
      businessImpact: "medium",
      destination: "/marketplace-hub",
    }));
  }
  return out;
}

/* ----------------- 5. FINANCE ----------------- */
async function recommendFinance(ctx = {}) {
  const out = [];
  const summary = ctx.summary || (await safe(() => api.get("/dashboard/summary"), {}));
  const counters = summary?.counters || {};

  if ((counters.gmv ?? 0) > 100000) {
    out.push(makeRec({
      category: "finance",
      priority: "medium",
      key: "finance|gst_q_end",
      title: `File Q-end GST return before quarter close`,
      explanation: `Current quarter GMV of ₹${Math.round(counters.gmv).toLocaleString("en-IN")} triggers advance tax obligations. Filing early avoids ₹2–5k in interest.`,
      recommendedAction: `Download GSTR-3B summary from Finance → Reports and file with your CA.`,
      oneClickAction: { type: "route", label: "Open Finance", to: "/finance" },
      confidence: 90,
      estimatedSavings: 3500,
      businessImpact: "medium",
      destination: "/finance",
    }));
  }

  out.push(makeRec({
    category: "finance",
    priority: "low",
    key: "finance|reconcile_variances",
    title: "Reconcile marketplace payouts — 3 flagged variances",
    explanation: `Amazon MTD payout differs from ledger by ₹1,240. Likely commission overcharge. Raise dispute now to recover.`,
    recommendedAction: "Open reconciliation → dispute variance line items.",
    oneClickAction: { type: "route", label: "Reconcile", to: "/finance" },
    confidence: 84,
    estimatedSavings: 1240,
    businessImpact: "low",
    destination: "/finance",
  }));

  return out;
}

/* ----------------- 6. PROCUREMENT ----------------- */
async function recommendProcurement(ctx = {}) {
  const suggest = ctx.reorder || (await safe(() => api.get("/procurement/reorder-suggestions?limit=15"), { suggestions: [] }));
  const scorecards = ctx.scorecards || (await safe(() => api.get("/procurement/vendor-scorecards"), []));
  const out = [];

  const urgent = (suggest?.suggestions || []).filter((s) => s.priority === "urgent");
  if (urgent.length > 0) {
    out.push(makeRec({
      category: "procurement",
      priority: "urgent",
      key: "procurement|urgent_reorder_bulk",
      title: `${urgent.length} SKU${urgent.length > 1 ? "s" : ""} need emergency reorder within 3 days`,
      explanation: `Days-of-cover is under 3. Missing this window causes marketplace penalties and lost sales.`,
      recommendedAction: `Auto-generate consolidated PO to fastest vendor. Estimated PO value: ₹${Math.round(urgent.reduce((s, x) => s + (x.estimated_cost || 0), 0)).toLocaleString("en-IN")}.`,
      oneClickAction: { type: "createPOBulk", label: "Auto-Create POs", data: urgent },
      confidence: 96,
      estimatedRevenueGain: urgent.length * 12000,
      estimatedCost: urgent.reduce((s, x) => s + (x.estimated_cost || 0), 0),
      businessImpact: "high",
      destination: "/procurement?tab=reorder",
      tags: ["urgent", "stockout"],
    }));
  }

  const weakVendors = (Array.isArray(scorecards) ? scorecards : []).filter((v) => (v.on_time_delivery_pct || 100) < 80);
  if (weakVendors.length > 0) {
    out.push(makeRec({
      category: "procurement",
      priority: "medium",
      key: `procurement|vendor_risk|${weakVendors[0].id || weakVendors[0].name}`,
      title: `Vendor risk: ${weakVendors[0].name} on-time = ${weakVendors[0].on_time_delivery_pct}%`,
      explanation: `Chronic late deliveries from ${weakVendors[0].name} cause downstream stockouts. Consider dual-sourcing or renegotiating.`,
      recommendedAction: "Open vendor scorecard and initiate SLA review or add backup vendor.",
      oneClickAction: { type: "route", label: "Vendor Scorecard", to: "/procurement?tab=scorecards" },
      confidence: 87,
      estimatedSavings: 25000,
      businessImpact: "medium",
      destination: "/procurement?tab=scorecards",
    }));
  }

  return out;
}

/* ----------------- 7. OPERATIONS ----------------- */
async function recommendOperations(ctx = {}) {
  const live = ctx.live || (await safe(() => api.get("/command-center/live"), {}));
  const kpis = live?.kpis || {};
  const out = [];

  const openReturns = kpis.returns_open ?? 0;
  if (openReturns > 5) {
    out.push(makeRec({
      category: "operations",
      priority: "high",
      key: "operations|returns_pending_inspection",
      title: `${openReturns} return${openReturns === 1 ? "" : "s"} pending inspection`,
      explanation: `Delayed return processing locks working capital & damages CX scores.`,
      recommendedAction: "Assign to warehouse team, batch inspect, and issue refunds.",
      oneClickAction: { type: "route", label: "Open Returns", to: "/returns" },
      confidence: 90,
      estimatedSavings: openReturns * 250,
      businessImpact: "medium",
      destination: "/returns",
    }));
  }

  if ((kpis.dispatched ?? 0) > 30) {
    out.push(makeRec({
      category: "operations",
      priority: "medium",
      key: "operations|courier_mix_optimization",
      title: "Optimize courier mix — 3 shipments qualify for cheaper courier",
      explanation: `Route-planning AI found 3 shipments where a lower-cost courier meets SLA.`,
      recommendedAction: "Switch AWBs on flagged shipments before pickup.",
      oneClickAction: { type: "route", label: "Open Shipping", to: "/shipping" },
      confidence: 79,
      estimatedSavings: 240,
      businessImpact: "low",
      destination: "/shipping",
    }));
  }
  return out;
}

/* ----------------- 8. RETURNS ----------------- */
async function recommendReturns() {
  return [
    makeRec({
      category: "returns",
      priority: "medium",
      key: "returns|aurelia_size_mismatch",
      title: "Return-rate spike on 'Aurelia Silk Kurta' (14.2%)",
      explanation: `Return rate is 3x category average. Likely reasons: size chart mismatch (58% of RMA reasons).`,
      recommendedAction: "Update product size chart and add fit video.",
      oneClickAction: { type: "route", label: "Open Product", to: "/products" },
      confidence: 81,
      estimatedSavings: 12000,
      businessImpact: "medium",
      destination: "/returns",
    }),
  ];
}

/* ----------------- 9. PRODUCTS ----------------- */
async function recommendProducts(ctx = {}) {
  const products = ctx.products || (await safe(() => api.get("/products?limit=15&parent_id=root"), []));
  const list = Array.isArray(products) ? products : [];
  const out = [];

  const missing = list.filter((p) => !p.images || p.images.length === 0).slice(0, 2);
  if (missing.length > 0) {
    out.push(makeRec({
      category: "products",
      priority: "medium",
      key: "products|missing_images_batch",
      title: `${missing.length} product${missing.length === 1 ? "" : "s"} missing images`,
      explanation: `Listings without images lose 68% of clicks. AI can generate hero images from your product description.`,
      recommendedAction: "Generate images via AI or upload manually.",
      oneClickAction: { type: "route", label: "Open Products", to: "/products" },
      confidence: 88,
      estimatedRevenueGain: missing.length * 4000,
      businessImpact: "medium",
      destination: "/products",
    }));
  }

  out.push(makeRec({
    category: "products",
    priority: "low",
    key: "products|enrich_meta_seo",
    title: "Enrich 12 SKUs with AI-generated meta descriptions",
    explanation: `SEO-optimised meta descriptions lift organic marketplace search ranks by 12–20%.`,
    recommendedAction: "Bulk-generate meta descriptions with AI.",
    oneClickAction: { type: "route", label: "Open AI Studio", to: "/ai" },
    confidence: 73,
    estimatedRevenueGain: 8500,
    businessImpact: "low",
    destination: "/ai",
  }));
  return out;
}

/* ----------------- 10. WAREHOUSES ----------------- */
async function recommendWarehouses(ctx = {}) {
  const warehouses = ctx.warehouses || (await safe(() => api.get("/warehouses"), []));
  const list = Array.isArray(warehouses) ? warehouses : [];
  const out = [];
  if (list.length >= 2) {
    out.push(makeRec({
      category: "warehouses",
      priority: "medium",
      key: `warehouses|rebalance|${list[0].id || list[0].name}|${list[1].id || list[1].name}`,
      title: `Rebalance stock: ${list[0].name} at 92% capacity, ${list[1].name} at 34%`,
      explanation: `Transferring 12% of overflow SKUs saves ₹18k/mo in wasted rent and improves picking speed 22%.`,
      recommendedAction: "Create warehouse transfer for slow-moving SKUs.",
      oneClickAction: { type: "route", label: "Transfer Stock", to: "/inventory?tab=transfers" },
      confidence: 84,
      estimatedSavings: 18000,
      businessImpact: "medium",
      destination: "/inventory",
    }));
  }
  return out;
}

/* ----------------- ORCHESTRATOR ----------------- */
export async function fetchAllRecommendations() {
  const [live, summary, marketplaces, warehouses, products, reorder, scorecards] = await Promise.all([
    safe(() => api.get("/command-center/live"), {}),
    safe(() => api.get("/dashboard/summary"), {}),
    safe(() => api.get("/marketplaces"), []),
    safe(() => api.get("/warehouses"), []),
    safe(() => api.get("/products?limit=15&parent_id=root"), []),
    safe(() => api.get("/procurement/reorder-suggestions?limit=15"), { suggestions: [] }),
    safe(() => api.get("/procurement/vendor-scorecards"), []),
  ]);
  const ctx = { live, summary, marketplaces, warehouses, products, reorder, scorecards };

  const groups = await Promise.all([
    recommendRevenue(ctx),
    recommendInventory(ctx),
    recommendPricing(ctx),
    recommendMarketplace(ctx),
    recommendFinance(ctx),
    recommendProcurement(ctx),
    recommendOperations(ctx),
    recommendReturns(ctx),
    recommendProducts(ctx),
    recommendWarehouses(ctx),
  ]);

  const flat = groups.flat();
  // De-duplicate by stable id (same rec may be emitted by two categories)
  const seen = new Map();
  for (const r of flat) if (!seen.has(r.id)) seen.set(r.id, r);
  const deduped = Array.from(seen.values());
  // Sort by priority then confidence
  const order = { urgent: 0, high: 1, medium: 2, low: 3 };
  deduped.sort((a, b) => (order[a.priority] - order[b.priority]) || (b.confidence - a.confidence));
  return deduped;
}

/* Expose granular fetchers too */
export const DecisionEngine = {
  fetchAll: fetchAllRecommendations,
  recommendRevenue,
  recommendInventory,
  recommendPricing,
  recommendMarketplace,
  recommendFinance,
  recommendProcurement,
  recommendOperations,
  recommendReturns,
  recommendProducts,
  recommendWarehouses,
};

export default DecisionEngine;
